import { type DateTimeValueReturnType, getDateRangeISO, reshapeSelectedFilters, sanitizeObjectEntries } from 'BreetComponents';
import {
	type DropdownOptionType,
	getQueryParamValue,
	type IPagination,
	toExportEndISODate,
	toISODate,
	updateUrlSearchParams,
} from 'BreetHelpers';
import { selectExportEndDate, selectExportStartDate, useAppSelector } from 'BreetRedux';
import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WithPaginatedFiltersProps } from './helpers';

const sortableFilters = ['createdAt', 'updatedAt'];

const parseFilterParam = (key: string): Record<string, string> | undefined => {
	const raw = getQueryParamValue(key);
	if (!raw) return undefined;
	try {
		return JSON.parse(raw) as Record<string, string>;
	} catch {
		return undefined;
	}
};

export const withPaginatedFilters = <P, D = object | undefined>(
	Component: ComponentType<P>,
	extraOptions: {
		extraSortableFilters?: string[];
		initialDateRange?: DateTimeValueReturnType;
		syncFiltersToUrl?: boolean;
	} = {
		extraSortableFilters: [],
		initialDateRange: getDateRangeISO(1),
	}
) => {
	const syncFiltersToUrl = extraOptions.syncFiltersToUrl ?? true;

	const EnhacedComponent = (props: P & Partial<WithPaginatedFiltersProps<D>>) => {
		const extraSortableFilters = useMemo(() => extraOptions.extraSortableFilters ?? [], []);

		const exportStartDate = useAppSelector(selectExportStartDate);
		const exportEndDate = useAppSelector(selectExportEndDate);

		const [reqPageNum, setReqPageNum] = useState(1);
		const [reqPageSize, setReqPageSize] = useState(50);

		const [reqFilter, setReqFilter] = useState<Record<string, string>>(() =>
			syncFiltersToUrl ? (parseFilterParam('filter') ?? {}) : {}
		);

		const [reqSearch, setReqSearch] = useState<Record<string, string>>(() =>
			syncFiltersToUrl ? (parseFilterParam('search') ?? {}) : {}
		);

		const [reqSorting, setReqSorting] = useState<Record<string, string>>(() =>
			syncFiltersToUrl ? (parseFilterParam('sort') ?? {}) : {}
		);
		const [checkedItems, setCheckedItems] = useState<D[]>([]);

		const didSyncInit = useRef(false);

		useEffect(() => {
			const page = getQueryParamValue('page');
			const size = getQueryParamValue('size');

			if (page) setReqPageNum(+page);
			if (size) setReqPageSize(+size);
		}, []);

		useEffect(() => {
			if (!syncFiltersToUrl) return;

			if (!didSyncInit.current) {
				didSyncInit.current = true;
				return;
			}

			const addParams: Record<string, string | number> = {};
			const deleteParams: string[] = [];

			(
				[
					['search', reqSearch],
					['filter', reqFilter],
					['sort', reqSorting],
				] as const
			).forEach(([key, value]) => {
				if (Object.keys(value).length) addParams[key] = JSON.stringify(value);
				else deleteParams.push(key);
			});

			updateUrlSearchParams({ addParams, deleteParams });
		}, [reqSearch, reqFilter, reqSorting]);

		const handleCheckboxChange = useCallback((data: D[]) => {
			setCheckedItems(data);
		}, []);

		const handlePageChange = useCallback(
			(state: Partial<IPagination>) => {
				const page = state.currentPage ?? 1;
				const size = state.pageLimit ?? reqPageSize;
				setReqPageNum(page);
				setReqPageSize(size);
				updateUrlSearchParams({ addParams: { page, size } });
			},
			[reqPageSize]
		);

		// A filter/search change resets to the first page — mirror that to the URL too, otherwise a reload
		// after filtering while on a later page would restore the stale page number and fetch the wrong slice.
		const resetToFirstPage = useCallback(() => {
			setReqPageNum(1);
			updateUrlSearchParams({ addParams: { page: 1 } });
		}, []);

		const handleSelectedFilterChange = useCallback(
			(selectedFilters?: Record<string, DropdownOptionType | undefined>) => {
				if (!selectedFilters) return;

				const sortingFilters: Record<string, DropdownOptionType> = {};
				const otherFilters: Record<string, DropdownOptionType> = {};

				Object.entries(selectedFilters).forEach(([filteredKey, filteredValue]) => {
					if (!filteredValue) return;
					if ([...sortableFilters, ...extraSortableFilters].includes(filteredKey)) sortingFilters[filteredKey] = filteredValue;
					else otherFilters[filteredKey] = filteredValue;
				});

				setReqSorting(reshapeSelectedFilters(sortingFilters));
				setReqFilter(reshapeSelectedFilters(otherFilters));

				resetToFirstPage();
			},
			[extraSortableFilters, resetToFirstPage]
		);

		const applyFilters = useCallback(
			(values: Record<string, string>) => {
				setReqFilter(Object.fromEntries(Object.entries(values).filter(([, value]) => value)));
				resetToFirstPage();
			},
			[resetToFirstPage]
		);

		const updateFilterData = (
			prevData: Record<string, string>,
			currentSearchKey: string,
			sanitizedData: Record<string, string>
		) => {
			const restOfPrevData = Object.fromEntries(Object.entries(prevData).filter(([key]) => key !== currentSearchKey));
			return { ...restOfPrevData, ...sanitizedData } as Record<string, string>;
		};

		const handleReqSearch = useCallback(
			(data: Record<string, string>) => {
				const sanitizedData = sanitizeObjectEntries(data);
				const searchKeys = Object.keys(data);
				const [currentSearchKey] = searchKeys;
				const strictFilterKeys = ['_id'];
				const searchIncludesFilterKey = searchKeys.some((key) => strictFilterKeys.includes(key));

				// Store the raw value — the transport layers encode exactly once (URLSearchParams for the browser
				// URL, fetchBaseQuery for the API request). Encoding here too double-encodes the request
				// (e.g. "we re" → "we%2520re"), so the backend searches for the literal escaped string.
				if (searchIncludesFilterKey) {
					setReqFilter((prevData) => updateFilterData(prevData, currentSearchKey, sanitizedData as Record<string, string>));
				} else {
					setReqSearch(sanitizedData as Record<string, string>);
				}

				resetToFirstPage();
			},
			[resetToFirstPage]
		);

		const exportsQuery = useMemo(
			() => ({
				startDate: exportStartDate ? toISODate(exportStartDate) : '',
				// Inclusive end: the backend filters `<=` at start-of-day, so send the next day's boundary.
				endDate: exportEndDate ? toExportEndISODate(exportEndDate) : '',
				exports: !!exportStartDate && !!exportEndDate,
			}),
			[exportEndDate, exportStartDate]
		);

		return (
			<Component
				{...props}
				reqPageNum={reqPageNum}
				reqPageSize={reqPageSize}
				reqFilter={reqFilter}
				reqSearch={reqSearch}
				reqSorting={reqSorting}
				checkedItems={checkedItems}
				handleCheckboxChange={handleCheckboxChange}
				handlePageChange={handlePageChange}
				handleSelectedFilterChange={handleSelectedFilterChange}
				applyFilters={applyFilters}
				handleReqSearch={handleReqSearch}
				exportsQuery={exportsQuery}
			/>
		);
	};

	return EnhacedComponent;
};
